import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Button, Tooltip } from '@bijie/react-bnb';
import { connect } from 'react-redux';

import withi18n from '~/utils/withI18n';
import { getUserInfo, getCurrentProgress, getProdcutTier } from '~/actions/actionsSet/recharge';
import { getLoginUrl } from '~/utils/urlUtils';
import { STATUS } from '../constants';
import { getLabelWithDefaultStr } from '~/utils/i18n';
import { formatNumber } from '~/utils/number';

import FlexBox from '~/components/FlexBox';
import Icon from '~/components/Icon';
import SharePopup from './SharePopup';
import DepositPopup from './DepositPopup';
import IconWithBG from '~/components/IconWithBG';

type Props = {
  rechargeProcut: Object,
  isLoggedIn: Boolean,
  i18n: Object,
  locale: String,
  isMobile: Boolean,
};

const mapStateToProps = ({ rechargeProcut, isLoggedIn }) => ({ rechargeProcut, isLoggedIn });

export default
@withi18n
@connect(mapStateToProps, {})
class Progress extends React.Component<Props> {
  _tooltip = React.createRef();
  _triangle = React.createRef();
  _bar = React.createRef();
  _hasGetInfo = false;

  state = {
    progress: 0,
    showSharePopup: false,
    showDepositPopup: false,
    yourTotalAirdrop: 0,
    yourTotalDeposit: 0,
    productTier: [],
    currentAirdrop: 0,
  };

  constructor(props) {
    super(props);
    props.onRef(this);
  }

  async componentDidUpdate(preProps) {
    const { productId: preId, proceedStatus: preStatus } = preProps.rechargeProcut;
    const { productId: currentId, proceedStatus: currentStatus } = this.props.rechargeProcut;
    if (preId !== currentId) {
      await this.getProdcutTier(currentId);
      this.getCurrentProgress(currentId);
    }
    if (currentId && this.props.isLoggedIn && !this._hasGetInfo) {
      this._hasGetInfo = true;
      this.getUserInfo();
    }
    if (preStatus !== currentStatus && currentStatus === STATUS.PROCESSING)
      this.timer = setInterval(() => {
        this.getCurrentProgress(this.props.rechargeProcut.productId);
      }, 10000);
    if (currentStatus === STATUS.FINISHED && this.timer) clearInterval(this.timer);
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  translate = (key, defaultVal) => {
    let pre = 'exchange-recharge-';
    if (key.includes('exchange')) pre = '';
    return getLabelWithDefaultStr(`${pre}${key}`, this.props.i18n, defaultVal);
  };

  getCurrentProgress = async productId => {
    const { productTier } = this.state;
    if (!productTier.length) return;
    let res = await getCurrentProgress(productId);
    if (!res) return;
    const { totalDepositCfg, totalDeposit: currentTotalDeposit, proceedStatus } = res;
    if (proceedStatus === STATUS.CLOSE) {
      if (this.timer) clearInterval(this.timer);
      window.location.reload();
      return;
    }
    const average = 1 / productTier.length;
    let progress = 0;

    let index = productTier.findIndex(item => item.sum > currentTotalDeposit);
    if (index < 0) index = productTier.length - 1;
    const currentAirdrop = productTier[index].airdropPercent;
    if (!index) {
      progress = (currentTotalDeposit / productTier[0].sum) * average;
    } else {
      const currentTarget =
        index === productTier.length - 1
          ? totalDepositCfg - productTier[index - 1].sum
          : productTier[index].takeDeposit;
      progress =
        index * average +
        ((currentTotalDeposit - productTier[index - 1].sum) / currentTarget) * average;
    }

    if (progress > 0.95 && proceedStatus === STATUS.PROCESSING) progress = 0.95;
    if (progress > 1) progress = 1;

    this.setState(
      {
        currentAirdrop,
        currentTotalDeposit,
        currentIndex: index,
        progress,
      },
      () => this.moveToolTip()
    );
  };

  getProdcutTier = async productId => {
    let productTier = await getProdcutTier(productId);
    if (!productTier) return;
    productTier.reduce((pre, item) => {
      let sum = pre + item.takeDeposit;
      item.sum = sum;
      return sum;
    }, 0);
    await this.setState({ productTier });
  };

  getUserInfo = async () => {
    const { rechargeProcut, isLoggedIn } = this.props;
    const { productId } = rechargeProcut;
    if (!productId || !isLoggedIn) return;
    let res = await getUserInfo(productId);
    if (!res) return;
    this.setState({
      yourTotalDeposit: res.totalDeposit,
      yourTotalAirdrop: res.totalAirdrop,
    });
  };

  moveToolTip = () => {
    const _tooltip = this._tooltip.current;
    const _triangle = this._triangle.current;
    const _bar = this._bar.current;
    const { progress } = this.state;
    let toolTipX = 0;
    let triangleX = 0;

    const pgLength = progress * _bar.offsetWidth;
    const maxTriangleX = _bar.offsetWidth - _triangle.offsetWidth;
    const maxToolTipX = _bar.offsetWidth - _tooltip.offsetWidth;
    triangleX = pgLength - _triangle.offsetWidth / 2;
    toolTipX = pgLength - _tooltip.offsetWidth / 2;
    if (triangleX > maxTriangleX) triangleX = maxTriangleX;
    if (toolTipX > maxToolTipX) toolTipX = maxToolTipX;
    if (triangleX > 0) _triangle.style.transform = `translate(${triangleX}px)`;
    if (toolTipX > 0) _tooltip.style.transform = `translate(${toolTipX}px)`;
  };

  goLogin = () => {
    const { locale } = this.props;
    window.location.href = getLoginUrl(locale, `/${locale}/recharge`);
  };

  goShare = type => {
    const url = window.location.href;
    let shareContent = this.props.rechargeProcut.shareContent;
    shareContent = JSON.parse(shareContent || '{}');
    shareContent = encodeURIComponent(shareContent[this.props.locale] || shareContent.en);
    if (type === 'weibo') {
      var link = `http://service.weibo.com/share/share.php?url=${url}&title=${shareContent}`;
    } else {
      var link = `https://twitter.com/intent/tweet?url=${url}&text=${shareContent}`;
    }
    window.open(link);
  };

  render() {
    const { props, state } = this;
    const { rechargeProcut, isLoggedIn, isMobile } = props;
    const { proceedStatus, name, image } = rechargeProcut;
    const allowDeposit = isLoggedIn && proceedStatus === STATUS.PROCESSING;

    let yourTotalAirdrop =
      state.yourTotalAirdrop || (isLoggedIn ? (proceedStatus === STATUS.PENDING ? '--' : 0) : '--');
    let yourTotalDeposit =
      state.yourTotalDeposit || (isLoggedIn ? (proceedStatus === STATUS.PENDING ? '--' : 0) : '--');

    return (
      <Wrapper>
        <GlobalStyle />
        <SharePopup
          onCancel={() => this.setState({ showSharePopup: false })}
          visible={state.showSharePopup}
          goShare={this.goShare}
          translate={this.translate}
        />
        {state.showDepositPopup && (
          <DepositPopup
            isMobile={isMobile}
            rechargeProcut={rechargeProcut}
            onCancel={() => this.setState({ showDepositPopup: false })}
            translate={this.translate}
            isLoggedIn={isLoggedIn}
          />
        )}
        <div className="content">
          <FlexBox justify="space-between">
            <div className="title">{this.translate('depositProgress')}</div>
            {isMobile ? (
              <div className="shareBox" onClick={() => this.setState({ showSharePopup: true })}>
                <Icon type="Share-xsvg" size={16} color="#F0B90B" />
                <span>{this.translate('shareActivity')}</span>
              </div>
            ) : (
              <Tooltip
                placement="bottom"
                trigger={['hover']}
                overlay={
                  <TooltipPopup>
                    <div className="popupTitle">{`${this.translate('shareNow')}!`}</div>
                    <div>
                      <Icon
                        type="twitter"
                        color="#1E91F1"
                        size={20}
                        onClick={() => this.goShare('twitter')}
                      />
                      <Icon
                        type="weibo"
                        color="#D22428"
                        size={20}
                        onClick={() => this.goShare('weibo')}
                      />
                    </div>
                  </TooltipPopup>
                }
              >
                <div className="shareBox">
                  <Icon type="Share-xsvg" size={16} color="#F0B90B" />
                  <span>{this.translate('shareActivity')}</span>
                </div>
              </Tooltip>
            )}
          </FlexBox>
          <div className="tooltip">
            <div className="content" ref={this._tooltip}>
              <div className="airdrop">
                {this.translate('currentAirdrop').replace(/X/, state.currentAirdrop * 100)}
              </div>
              <div className="deposit">{`${this.translate(
                'currentAmount'
              )}: ${state.currentTotalDeposit || 0} ${name}`}</div>
            </div>

            <div className="triangle" ref={this._triangle} />
          </div>
          <div className="bar" ref={this._bar}>
            <BgColor width={state.progress} className="bgColor" />
            {state.productTier.map((item, index) => (
              <div
                className="percent"
                key={index}
                style={{
                  color: `${
                    state.currentIndex > index || state.progress === 1 ? 'white' : '#48515d'
                  }`,
                }}
              >
                {isMobile
                  ? `${item.airdropPercent * 100}%`
                  : this.translate('percent').replace(/{Amount}/, item.airdropPercent * 100)}
              </div>
            ))}
          </div>
          <div className="mark">
            {state.productTier.map((item, index) => (
              <Amount className="amount" key={index} length={state.productTier.length}>{`${
                state.productTier.length === index + 1 ? '>' : ''
              } ${
                state.productTier[index - 1] ? formatNumber(state.productTier[index - 1].sum) : 0
              } ${name}`}</Amount>
            ))}
          </div>
          {(!isLoggedIn || proceedStatus !== STATUS.FINISHED) && (
            <FlexBox justify={allowDeposit && 'space-between'} align="center">
              {isLoggedIn ? (
                <div className="summary" style={{ marginRight: !allowDeposit && '50px' }}>
                  <div className="text">{this.translate('myDepositAmount')}</div>
                  <div className="number">{`${yourTotalDeposit} ${name}`}</div>
                </div>
              ) : (
                <div className="goLogin" onClick={this.goLogin} style={{ marginRight: '50px' }}>
                  {this.translate('goLogin')}
                </div>
              )}

              <div className="summary">
                <div className="text">{this.translate('receivedReward')}</div>
                <div className="number">{`${yourTotalAirdrop} ${name}`}</div>
              </div>
              {allowDeposit && (
                <Button
                  type="primary"
                  className="depositButton"
                  onClick={() => this.setState({ showDepositPopup: true })}
                >
                  {this.translate('nowDeposit')}
                </Button>
              )}
            </FlexBox>
          )}
          {isLoggedIn && proceedStatus === STATUS.FINISHED && (
            <div className="highlightEnd">
              <div className="endReceived">
                {this.translate('receivedReward')}
                <FlexBox align="center">
                  <IconWithBG width={30} bgColor="#F6F6F6" src={image} margin="4px 8px" />
                  {`${yourTotalAirdrop} ${name}`}
                </FlexBox>
              </div>
              <div className="endDepoist">
                <span>{this.translate('myDepositAmount')}</span>
                <span>{`${yourTotalDeposit} ${name}`}</span>
              </div>
            </div>
          )}
        </div>
      </Wrapper>
    );
  }
}

const GlobalStyle = createGlobalStyle`
  .bnc-tooltip-inner {
    box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.1);
  }
`;
const BgColor = styled.div.attrs(({ className }) => ({
  className,
}))`
  width: ${({ width }) => `${width * 100}%`};
  border-radius: ${({ width }) => width >= 1 && '24px'}};
  padding: ${({ width }) => (width >= 1 ? '2px' : '2px 0 2px 2px')}};
`;
const Amount = styled.div.attrs(({ className }) => ({
  className,
}))`
  width: ${({ length }) => `${(1 / length) * 100}%`};
`;

const TooltipPopup = styled.div`
  width: 136px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: #fff;
  border-radius: 4px;
  > .popupTitle {
    font-size: 14px;
    margin-bottom: 16px;
  }
  i {
    cursor: pointer;
  }
  i:first-child {
    margin-right: 24px;
  }
`;

const Wrapper = styled.div`
  padding: 0 16px;
  margin: 0 auto;
  width: 100%;
  > .content {
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.05);
    border-radius: 3px;
    background: #fff;
    padding: 24px 16px;
    margin-top: -20px;
    .title {
      font-weight: 500;
      font-size: 16px;
    }
    .shareBox {
      display: flex;
      align-items: center;
      color: #f0b90b;
      > span {
        margin-left: 8px;
      }
    }
    > .tooltip {
      position: relative;
      margin: 8px 0 16px;
      > .content {
        box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.1);
        padding: 8px;
        background: #ffffff;
        display: inline-block;
        > .airdrop {
          font-size: 13px;
          white-space: nowrap;
        }
        > .deposit {
          color: #848e9c;
          font-weight: 300;
          font-size: 12px;
          white-space: nowrap;
        }
      }

      > .triangle {
        width: 0;
        height: 0;
        border: 10px solid transparent;
        border-top: 8px solid white;
        position: absolute;
        bottom: -18px;
      }
    }
    > .bar {
      background: rgba(234, 236, 239, 0.5);
      border-radius: 24px;
      height: 32px;
      display: flex;
      margin-bottom: 10px;
      position: relative;
      overflow: hidden;
      > .bgColor {
        background: linear-gradient(90deg, #f0b90b 0%, #f06728 121.96%);
        background-clip: content-box;
        border-top-left-radius: 24px;
        border-bottom-left-radius: 24px;
        height: 100%;
        position: absolute;
      }
      > .percent {
        z-index: 9;
        font-size: 12px;
        height: 32px;
        line-height: 32px;
        box-sizing: content-box;
        padding-bottom: 4px;
        flex: 1;
        text-align: center;
        &:not(:last-child) {
          border-right: 1px dashed rgba(132, 142, 156, 0.3);
        }
      }
    }
    > .mark {
      display: flex;
      margin-bottom: 100px;
      > .amount {
        flex-shrink: 0;
        transform-origin: left center;
        transform: rotate(60deg);
        color: #848e9c;
        font-size: 12px;
        opacity: 0.5;
        white-space: nowrap;
      }
    }
    .summary {
      > .text {
        color: #848e9c;
        font-size: 12px;
        margin-bottom: 8px;
      }
      > .number {
        font-size: 16px;
      }
    }
    .goLogin {
      color: #f0b90b;
      font-size: 16px;
      cursor: pointer;
      margin-bottom: 16px;
      white-space: nowrap;
    }
    .depositButton {
      width: 100%;
      margin-top: 24px;
      font-size: 14px;
    }
    > .highlightEnd {
      background: rgba(234, 236, 239, 0.3);
      padding: 16px 50px;
      font-size: 16px;
      > .endReceived {
        display: flex;
        align-items: center;
        font-weight: 500;
        flex-wrap: wrap;
        white-space: nowrap;
        justify-content: center;
        margin-bottom: 10px;
        font-size: 24px;
      }
      > .endDepoist {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        white-space: nowrap;
        justify-content: center;
        > span:first-child {
          color: #848e9c;
          margin-right: 8px;
        }
      }
    }
  }
  @media (min-width: 768px) {
    > .content {
      padding: 32px 24px;
      margin-top: -30px;
      > .tooltip {
        margin: 12px 0 16px;
      }
      > .bar {
        > .percent {
          font-size: 16px;
        }
      }
      > .mark {
        margin-bottom: 30px;
        > .amount {
          transform: translate(-50%);
          &:first-child {
            transform: none;
          }
          &:not(:first-child) {
            text-align: center;
          }
        }
      }
      .summary {
        > .text {
          font-size: 16px;
        }
      }
      .goLogin {
        margin-bottom: 0px;
      }
      .depositButton {
        width: 200px;
        margin-top: 0px;
      }
    }
  }
  @media (min-width: 1260px) {
    width: 1260px;
    padding: 0px;
    > .content {
      padding: 32px 154px;
      margin-top: -84px;
      .summary {
        display: flex;
        > .text {
          margin-bottom: 0px;
        }
        > .number {
          margin-left: 24px;
        }
      }
    }
  }
`;
